import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertTriangle,
  Check,
  Copy,
  ExternalLink,
  Globe,
  Image as ImageIcon,
  Loader2,
  Plus,
  Trash2,
  Upload,
  Video,
  X,
} from 'lucide-react'

import { api } from './api/client'
import RichMenuSection from './RichMenuSection'
import { ApiError } from './api/types'
import type { PublicBookingState, PublicBookingUpdate } from './api/types'
import { Button } from './components/ui/button'
import { Card, CardContent } from './components/ui/card'
import { Input } from './components/ui/input'
import { Label } from './components/ui/label'
import { Switch } from './components/ui/switch'
import { toast } from './components/ui/use-toast'

const MAX_GALLERY = 8
const MAX_UPLOAD_BYTES = 8 * 1024 * 1024
const ALLOWED_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

export default function BookingPagePanel() {
  const { t } = useTranslation()
  const [state, setState] = useState<PublicBookingState | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const [slug, setSlug] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [englishEnabled, setEnglishEnabled] = useState(false)
  const [displayNameZh, setDisplayNameZh] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [taglineZh, setTaglineZh] = useState('')
  const [tagline, setTagline] = useState('')

  // Phase 6A.5 — identity pack
  const [logoUrl, setLogoUrl] = useState<string | null>(null)
  const [accentColor, setAccentColor] = useState('#2f7a55')
  const [coverPhotos, setCoverPhotos] = useState<string[]>([])
  // SHARED-1 feature flags
  const [addOnsEnabled, setAddOnsEnabled] = useState(false)
  const [photoCardsEnabled, setPhotoCardsEnabled] = useState(false)
  const [taxInclusive, setTaxInclusive] = useState(true)
  const [videoUrl, setVideoUrl] = useState('')
  const [requireDeposit, setRequireDeposit] = useState(false)
  const [depositPercent, setDepositPercent] = useState(30)
  const [depositLink, setDepositLink] = useState('')
  const [logoUploading, setLogoUploading] = useState(false)
  const [galleryUploading, setGalleryUploading] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)

  function hydrate(s: PublicBookingState) {
    setState(s)
    setSlug(s.slug ?? '')
    setEnabled(s.enabled)
    setEnglishEnabled(s.english_enabled)
    setDisplayNameZh(s.display_name_zh ?? '')
    setDisplayName(s.display_name ?? '')
    setTaglineZh(s.tagline_zh ?? '')
    setTagline(s.tagline ?? '')
    setLogoUrl(s.logo_url ?? null)
    setAccentColor(s.accent_color ?? '#2f7a55')
    setCoverPhotos(s.cover_photos ?? [])
    setVideoUrl(s.intro_video_url ?? '')
    setAddOnsEnabled(s.add_ons_enabled ?? false)
    setPhotoCardsEnabled(s.photo_cards_enabled ?? false)
    setTaxInclusive(s.tax_inclusive ?? true)
    setRequireDeposit(s.require_deposit ?? false)
    setDepositPercent(s.deposit_percent ?? 30)
    setDepositLink(s.deposit_link ?? '')
  }

  useEffect(() => {
    let alive = true
    setLoading(true)
    api
      .getPublicBooking()
      .then((s) => alive && hydrate(s))
      .catch((e: unknown) => alive && setErr(e instanceof Error ? e.message : t('errors.generic')))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [t])

  function diffPatch(): PublicBookingUpdate {
    if (!state) return {}
    const patch: PublicBookingUpdate = {}
    const slugNorm = slug.trim().toLowerCase()
    if (slugNorm !== (state.slug ?? '')) patch.slug = slugNorm || null
    if (enabled !== state.enabled) patch.enabled = enabled
    if (englishEnabled !== state.english_enabled) patch.english_enabled = englishEnabled
    if (displayNameZh !== (state.display_name_zh ?? '')) patch.display_name_zh = displayNameZh || null
    if (displayName !== (state.display_name ?? '')) patch.display_name = displayName || null
    if (taglineZh !== (state.tagline_zh ?? '')) patch.tagline_zh = taglineZh || null
    if (tagline !== (state.tagline ?? '')) patch.tagline = tagline || null
    // Identity pack — only include in the patch if it actually changed so
    // we don't accidentally re-clear fields that a concurrent edit set.
    if (logoUrl !== (state.logo_url ?? null)) patch.logo_url = logoUrl
    if (accentColor !== (state.accent_color ?? '#2f7a55')) patch.accent_color = accentColor
    if (!sameStringList(coverPhotos, state.cover_photos ?? [])) patch.cover_photos = coverPhotos
    if (videoUrl.trim() !== (state.intro_video_url ?? '')) {
      patch.intro_video_url = videoUrl.trim() || null
    }
    if (addOnsEnabled !== (state.add_ons_enabled ?? false)) patch.add_ons_enabled = addOnsEnabled
    if (photoCardsEnabled !== (state.photo_cards_enabled ?? false))
      patch.photo_cards_enabled = photoCardsEnabled
    if (taxInclusive !== (state.tax_inclusive ?? true)) patch.tax_inclusive = taxInclusive
    if (requireDeposit !== (state.require_deposit ?? false)) patch.require_deposit = requireDeposit
    if (depositPercent !== (state.deposit_percent ?? 30)) patch.deposit_percent = depositPercent
    if (depositLink.trim() !== (state.deposit_link ?? '')) {
      patch.deposit_link = depositLink.trim() || null
    }
    return patch
  }

  const [brandingSavedAt, setBrandingSavedAt] = useState<number | null>(null)

  async function save() {
    const patch = diffPatch()
    if (!Object.keys(patch).length) return
    setBusy(true)
    setErr(null)
    try {
      const next = await api.updatePublicBooking(patch)
      hydrate(next)
      if ('logo_url' in patch || 'accent_color' in patch) setBrandingSavedAt(Date.now())
      toast({ title: t('bookingPage.saved'), variant: 'success' })
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError && e.message
          ? e.message
          : e instanceof Error
            ? e.message
            : t('errors.generic')
      setErr(msg)
    } finally {
      setBusy(false)
    }
  }

  async function copyUrl() {
    if (!state?.public_url) return
    try {
      await navigator.clipboard.writeText(state.public_url)
      setCopied(true)
      setTimeout(() => setCopied(false), 1400)
    } catch {
      /* clipboard blocked */
    }
  }

  function validateImageFile(f: File): string | null {
    if (f.size > MAX_UPLOAD_BYTES) return t('bookingPage.uploadTooLarge')
    if (!ALLOWED_MIMES.includes(f.type)) return t('bookingPage.uploadBadType')
    return null
  }

  async function handleLogoUpload(file: File | null) {
    if (!file) return
    const e = validateImageFile(file)
    if (e) {
      toast({ title: e, variant: 'destructive' })
      return
    }
    setLogoUploading(true)
    setErr(null)
    try {
      const publicUrl = await api.uploadClinicAsset('logo', file)
      setLogoUrl(publicUrl)
    } catch {
      toast({ title: t('bookingPage.uploadFailed'), variant: 'destructive' })
    } finally {
      setLogoUploading(false)
      if (logoInputRef.current) logoInputRef.current.value = ''
    }
  }

  async function handleGalleryUpload(files: FileList | null) {
    if (!files || files.length === 0) return
    const room = MAX_GALLERY - coverPhotos.length
    if (room <= 0) {
      toast({ title: t('bookingPage.galleryHint'), variant: 'destructive' })
      return
    }
    const accepted = Array.from(files).slice(0, room)
    setGalleryUploading(true)
    setErr(null)
    let added: string[] = []
    try {
      for (const f of accepted) {
        const ve = validateImageFile(f)
        if (ve) {
          toast({ title: `${f.name}: ${ve}`, variant: 'destructive' })
          continue
        }
        const url = await api.uploadClinicAsset('gallery', f)
        added.push(url)
      }
      if (added.length) setCoverPhotos((prev) => [...prev, ...added].slice(0, MAX_GALLERY))
    } catch {
      toast({ title: t('bookingPage.uploadFailed'), variant: 'destructive' })
    } finally {
      setGalleryUploading(false)
      if (galleryInputRef.current) galleryInputRef.current.value = ''
    }
  }

  function removePhoto(url: string) {
    setCoverPhotos((prev) => prev.filter((u) => u !== url))
  }

  function removeLogo() {
    setLogoUrl(null)
  }

  const dirty = Object.keys(diffPatch()).length > 0
  const hasSlug = !!state?.slug
  const isLive = !!state?.enabled && hasSlug

  return (
    <section className="space-y-3">
      <div className="space-y-0.5">
        <h2 className="text-base font-semibold tracking-tight">{t('bookingPage.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('bookingPage.sub')}</p>
      </div>

      {/* State banner */}
      {loading ? (
        <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          <Loader2 className="mr-2 inline size-4 animate-spin" />
          {t('loading')}
        </div>
      ) : (
        <div
          className={`flex items-start gap-3 rounded-lg border px-4 py-3 ${
            isLive
              ? 'border-success/30 bg-success/10 text-success'
              : 'border-warning/30 bg-warning/10 text-warning'
          }`}
        >
          {isLive ? (
            <Globe className="size-5 shrink-0 translate-y-0.5" />
          ) : (
            <AlertTriangle className="size-5 shrink-0 translate-y-0.5" />
          )}
          <div className="min-w-0 flex-1">
            <div className="break-all text-sm font-semibold">
              {isLive
                ? `${t('bookingPage.stateLive')} ${state.public_url}`
                : !hasSlug
                  ? t('bookingPage.stateNoSlug')
                  : t('bookingPage.stateDisabled')}
            </div>
            {isLive && state?.public_url && (
              <div className="mt-2 flex flex-wrap gap-2">
                <Button asChild variant="outline" size="sm">
                  <a href={state.public_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="size-3.5" />
                    {t('bookingPage.visit')}
                  </a>
                </Button>
                <Button variant="outline" size="sm" onClick={copyUrl}>
                  {copied ? (
                    <>
                      <Check className="size-3.5" /> {t('bookingPage.copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="size-3.5" /> {t('bookingPage.copy')}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form */}
      <Card>
        <CardContent className="space-y-5 p-5">
          {/* Slug */}
          <div className="space-y-1.5">
            <Label htmlFor="slug">{t('bookingPage.slugLabel')}</Label>
            <div className="flex items-stretch overflow-hidden rounded-md border border-input bg-card focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30">
              <span className="select-none border-r border-input bg-secondary/60 px-3 py-2 text-sm text-muted-foreground">
                book.petpro.com.tw/
              </span>
              <input
                id="slug"
                type="text"
                value={slug}
                placeholder={t('bookingPage.slugPlaceholder')}
                onChange={(e) => {
                  // Mirror backend _SLUG_RE: Latin alphanumeric + Han +
                  // Hiragana + Katakana + Hangul + hyphen. Strip anything
                  // else so the field always shows a legal-shaped value.
                  const cleaned = e.target.value.replace(
                    /[^a-zA-Z0-9一-鿿぀-ゟ゠-ヿ가-힯-]/g,
                    '',
                  )
                  // Only lowercase the ASCII letters — Unicode lowercase can
                  // change grapheme count (e.g. Turkish dotted-I), and CJK
                  // / Hangul have no case.
                  setSlug(cleaned.replace(/[A-Z]/g, (c) => c.toLowerCase()))
                }}
                disabled={busy}
                className="flex-1 bg-transparent px-3 py-2 font-mono text-sm outline-none placeholder:text-muted-foreground/60 disabled:opacity-50"
              />
            </div>
            <p className="text-xs text-muted-foreground/80">{t('bookingPage.slugHint')}</p>
          </div>

          {/* Toggles */}
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-0.5">
              <div className="text-sm font-medium">{t('bookingPage.enabled')}</div>
              <div className="text-xs text-muted-foreground">{t('bookingPage.enabledHint')}</div>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} disabled={busy} />
          </div>
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-0.5">
              <div className="text-sm font-medium">{t('bookingPage.englishEnabled')}</div>
              <div className="text-xs text-muted-foreground">{t('bookingPage.englishHint')}</div>
            </div>
            <Switch
              checked={englishEnabled}
              onCheckedChange={setEnglishEnabled}
              disabled={busy}
            />
          </div>

          {/* Features (SHARED-1) */}
          <div className="rounded-md border bg-muted/30 p-3 space-y-3">
            <div className="text-sm font-semibold text-muted-foreground">
              {t('bookingPage.featuresHeading')}
            </div>
            <div
              className="flex items-start justify-between gap-3"
              data-testid="feature-toggle-addons"
            >
              <div className="space-y-0.5">
                <div className="text-sm font-medium">
                  {t('bookingPage.addOnsEnabled')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('bookingPage.addOnsHint')}
                </div>
              </div>
              <Switch
                checked={addOnsEnabled}
                onCheckedChange={setAddOnsEnabled}
                disabled={busy}
              />
            </div>
            <div
              className="flex items-start justify-between gap-3"
              data-testid="feature-toggle-photo-cards"
            >
              <div className="space-y-0.5">
                <div className="text-sm font-medium">
                  {t('bookingPage.photoCardsEnabled')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('bookingPage.photoCardsHint')}
                </div>
              </div>
              <Switch
                checked={photoCardsEnabled}
                onCheckedChange={setPhotoCardsEnabled}
                disabled={busy}
              />
            </div>
            <div
              className="flex items-start justify-between gap-3"
              data-testid="feature-toggle-tax-inclusive"
            >
              <div className="space-y-0.5">
                <div className="text-sm font-medium">
                  {t('bookingPage.taxInclusive')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('bookingPage.taxInclusiveHint')}
                </div>
              </div>
              <Switch
                checked={taxInclusive}
                onCheckedChange={setTaxInclusive}
                disabled={busy}
              />
            </div>
          </div>

          {/* 預約訂金 (E3) — merchant chooses whether to collect and how much. */}
          <div className="space-y-3 rounded-lg border p-4" data-testid="deposit-card">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-0.5">
                <div className="text-sm font-medium">
                  {t('bookingPage.requireDeposit')}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('bookingPage.requireDepositHint')}
                </div>
              </div>
              <Switch
                checked={requireDeposit}
                onCheckedChange={setRequireDeposit}
                disabled={busy}
                data-testid="deposit-toggle"
              />
            </div>

            {requireDeposit && (
              <div className="space-y-3 border-t pt-3">
                <div className="space-y-1.5">
                  <Label htmlFor="deposit-pct">
                    {t('bookingPage.depositPercent')}
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="deposit-pct"
                      type="number"
                      min={0}
                      max={100}
                      value={depositPercent}
                      onChange={(e) =>
                        setDepositPercent(
                          Math.max(0, Math.min(100, Number(e.target.value) || 0)),
                        )
                      }
                      disabled={busy}
                      className="w-24"
                      data-testid="deposit-percent"
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('bookingPage.depositPercentHint')}
                  </p>
                </div>

                {/* How the deposit actually gets collected. */}
                {state?.deposit_gateway_ready ? (
                  <p
                    className="text-xs text-emerald-700 dark:text-emerald-400"
                    data-testid="deposit-gateway-ready"
                  >
                    {t('bookingPage.depositGatewayReady')}
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    <Label htmlFor="deposit-link">
                      {t('bookingPage.depositLink')}
                    </Label>
                    <Input
                      id="deposit-link"
                      value={depositLink}
                      onChange={(e) => setDepositLink(e.target.value)}
                      placeholder="https://line.me/pay/..."
                      disabled={busy}
                      data-testid="deposit-link"
                    />
                    <p className="text-xs text-muted-foreground">
                      {t('bookingPage.depositLinkHint')}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Names */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="dnz">{t('bookingPage.displayNameZh')}</Label>
              <Input
                id="dnz"
                value={displayNameZh}
                onChange={(e) => setDisplayNameZh(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dn">{t('bookingPage.displayName')}</Label>
              <Input
                id="dn"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tgz">{t('bookingPage.taglineZh')}</Label>
              <Input
                id="tgz"
                value={taglineZh}
                onChange={(e) => setTaglineZh(e.target.value)}
                disabled={busy}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tg">{t('bookingPage.tagline')}</Label>
              <Input
                id="tg"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                disabled={busy}
              />
            </div>
          </div>

          {/* ---- Identity pack: logo + accent + gallery + video ---- */}
          <div className="space-y-1 border-t border-border pt-5">
            <div className="text-sm font-semibold">{t('bookingPage.identityHeading')}</div>
            <p className="text-xs text-muted-foreground">{t('bookingPage.identitySub')}</p>
          </div>

          {/* Logo + accent color on one row */}
          <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
            <div className="space-y-1.5">
              <Label>{t('bookingPage.logo')}</Label>
              <div className="flex items-center gap-3">
                <div className="grid size-20 place-items-center overflow-hidden rounded-lg border border-dashed border-border bg-secondary/40">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={t('bookingPage.logoAlt')}
                      className="size-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="size-7 text-muted-foreground/50" aria-hidden />
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept={ALLOWED_MIMES.join(',')}
                    className="hidden"
                    onChange={(e) => handleLogoUpload(e.target.files?.[0] ?? null)}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy || logoUploading}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {logoUploading ? (
                      <>
                        <Loader2 className="size-3.5 animate-spin" />
                        {t('bookingPage.uploading')}
                      </>
                    ) : (
                      <>
                        <Upload className="size-3.5" />
                        {logoUrl ? t('bookingPage.addPhoto') : t('bookingPage.addPhoto')}
                      </>
                    )}
                  </Button>
                  {logoUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeLogo}
                      disabled={busy}
                    >
                      <Trash2 className="size-3.5" /> {t('bookingPage.removePhoto')}
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground/80">{t('bookingPage.logoHint')}</p>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="accent">{t('bookingPage.accentColor')}</Label>
              <div className="flex items-center gap-3">
                <input
                  id="accent"
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  disabled={busy}
                  className="h-10 w-14 cursor-pointer rounded-md border border-input bg-card p-1 shadow-sm"
                />
                <Input
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  disabled={busy}
                  className="max-w-[150px] font-mono"
                  placeholder="#2f7a55"
                />
              </div>
              <p className="text-xs text-muted-foreground/80">
                {t('bookingPage.accentColorHint')}
              </p>
            </div>
          </div>

          {/* Gallery */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>
                {t('bookingPage.gallery')}{' '}
                <span className="ml-1 font-normal text-muted-foreground/70">
                  ({coverPhotos.length} / {MAX_GALLERY})
                </span>
              </Label>
              <input
                ref={galleryInputRef}
                type="file"
                multiple
                accept={ALLOWED_MIMES.join(',')}
                className="hidden"
                onChange={(e) => handleGalleryUpload(e.target.files)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={
                  busy || galleryUploading || coverPhotos.length >= MAX_GALLERY
                }
                onClick={() => galleryInputRef.current?.click()}
              >
                {galleryUploading ? (
                  <>
                    <Loader2 className="size-3.5 animate-spin" />
                    {t('bookingPage.uploading')}
                  </>
                ) : (
                  <>
                    <Plus className="size-3.5" />
                    {t('bookingPage.addPhoto')}
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground/80">{t('bookingPage.galleryHint')}</p>
            {coverPhotos.length > 0 && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {coverPhotos.map((url, i) => (
                  <div
                    key={url}
                    className="group relative aspect-square overflow-hidden rounded-md border border-border bg-secondary/40"
                  >
                    <img src={url} alt="" className="size-full object-cover" />
                    {i === 0 && (
                      <span className="absolute left-1 top-1 rounded-sm bg-foreground/70 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-background">
                        Hero
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(url)}
                      disabled={busy}
                      aria-label={t('bookingPage.removePhoto')}
                      className="absolute right-1 top-1 grid size-6 place-items-center rounded-full bg-foreground/70 text-background opacity-0 transition-opacity hover:bg-destructive group-hover:opacity-100"
                    >
                      <X className="size-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Video */}
          <div className="space-y-1.5">
            <Label htmlFor="video">{t('bookingPage.video')}</Label>
            <div className="flex items-stretch gap-2">
              <div className="grid size-10 shrink-0 place-items-center rounded-md border border-input bg-secondary/60 text-muted-foreground">
                <Video className="size-4" />
              </div>
              <Input
                id="video"
                type="url"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder={t('bookingPage.videoPlaceholder')}
                disabled={busy}
              />
              {videoUrl && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setVideoUrl('')}
                  disabled={busy}
                  aria-label={t('bookingPage.videoClear')}
                >
                  <X className="size-4" />
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground/80">{t('bookingPage.videoHint')}</p>
          </div>

          {err && (
            <div
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {err}
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={save} disabled={busy || !dirty}>
              {busy ? (
                <>
                  <Loader2 className="size-4 animate-spin" />
                  {t('bookingPage.saving')}
                </>
              ) : (
                t('bookingPage.save')
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <RichMenuSection brandingSavedAt={brandingSavedAt} />
    </section>
  )
}

// Compare two string lists by position (order matters — gallery order
// determines which photo becomes the hero, so reordering IS a real edit).
function sameStringList(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false
  return true
}
